import type {
  OrganizationAddress,
  OrganizationPresentation,
} from "../../../api/public/presentation"
import type { OrgPresentationMode } from "../_shared/_logic/presentation-mode"

export type OrgPresentationSeoPage =
  | "profile"
  | "assist"
  | "business"
  | "privacy"
  | "contactMethods"

export type OrgPresentationSeoModel = {
  title: string
  description: string
  canonicalUrl: string
  robots: "index,follow" | "noindex,follow"
  jsonLd: Record<string, unknown> | null
}

type BuildOrgPresentationSeoModelArgs = {
  organization: OrganizationPresentation | null
  presentationMode: OrgPresentationMode
  hasCode: boolean
  page: OrgPresentationSeoPage
  presentationBasePath: string
  origin?: string | null
}

const normalizeText = (value?: string | null): string | null => {
  const text = value?.trim()

  return text || null
}

const stripQuery = (path: string): string =>
  path.split("?")[0]?.replace(/\/+$/, "") || "/presentation"

const resolveOrigin = (origin?: string | null): string => {
  const explicit = origin?.trim()

  if (explicit) return explicit.replace(/\/+$/, "")

  if (typeof window !== "undefined") {
    return window.location.origin.replace(/\/+$/, "")
  }

  return ""
}

const buildCanonicalUrl = ({
  origin,
  presentationBasePath,
  page,
}: {
  origin?: string | null
  presentationBasePath: string
  page: OrgPresentationSeoPage
}) => {
  const basePath = stripQuery(presentationBasePath)
  const path =
    page === "assist"
      ? `${basePath}/kehrassist`
      : page === "business"
        ? `${basePath}/betrieb`
        : page === "privacy"
          ? `${basePath}/datenschutz`
          : page === "contactMethods"
            ? `${basePath}/kontaktmittel`
            : basePath

  return `${resolveOrigin(origin)}${path}`
}

const buildDescription = ({
  organization,
  presentationMode,
  hasCode,
  page,
}: {
  organization: OrganizationPresentation | null
  presentationMode: OrgPresentationMode
  hasCode: boolean
  page: OrgPresentationSeoPage
}) => {
  const orgLabel = normalizeText(organization?.name) ?? "dem Betrieb"

  if (page === "assist") {
    return `Mit KehrAssist einen Schornsteinfegertermin bei ${orgLabel} vereinbaren, verschieben oder ein Anliegen klären.`
  }

  if (page === "contactMethods") {
    return `Kontaktmittel zur Terminabstimmung mit ${orgLabel} prüfen, Kontaktwege abbestellen oder Löschung anfragen.`
  }

  if (page === "privacy") {
    return `Datenschutzhinweise zur Terminabstimmung mit ${orgLabel}.`
  }

  if (page === "business") {
    return `Kontaktdaten, Standort, Öffnungszeiten und Hilfe zur Terminseite von ${orgLabel}.`
  }

  if (hasCode) {
    return `Ihr Terminlink für ${orgLabel} wurde erkannt. Bestätigen, verschieben oder bearbeiten Sie den Termin direkt im Browser.`
  }

  if (presentationMode === "appointment_portal_only") {
    return `Schornsteinfegertermin bei ${orgLabel} online bestätigen oder verschieben. Ohne App und ohne Kundenkonto.`
  }

  return `Schornsteinfegertermin bei ${orgLabel} online abstimmen oder ein Anliegen über KehrAssist klären. Ohne App und ohne Kundenkonto.`
}

const buildAddressJsonLd = (address?: OrganizationAddress | null) => {
  if (!address) return null

  const streetLine = [address.street, address.house_number]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ")
  const payload = {
    "@type": "PostalAddress",
    streetAddress: streetLine || undefined,
    postalCode: normalizeText(address.zip) ?? undefined,
    addressLocality: normalizeText(address.city) ?? undefined,
    addressCountry: normalizeText(address.country) ?? "DE",
  }

  return payload.streetAddress || payload.postalCode || payload.addressLocality ? payload : null
}

const SCHEMA_WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const

const buildOpeningHoursJsonLd = (organization: OrganizationPresentation | null) => {
  const hours = organization?.service_hours?.hours ?? []

  return hours.flatMap((day) =>
    (day.windows ?? []).flatMap((window) => {
      const opens = normalizeText(window.start_time)
      const closes = normalizeText(window.end_time)
      const dayOfWeek = SCHEMA_WEEKDAY_NAMES[day.weekday]

      if (!opens || !closes || !dayOfWeek) return []

      return [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek,
          opens,
          closes,
        },
      ]
    }),
  )
}

const buildLocalBusinessJsonLd = ({
  organization,
  canonicalUrl,
}: {
  organization: OrganizationPresentation | null
  canonicalUrl: string
}) => {
  if (!organization) return null

  const address = buildAddressJsonLd(organization.address)
  const openingHours = buildOpeningHoursJsonLd(organization)

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: normalizeText(organization.name) ?? "KehrBlatt Terminseite",
    url: canonicalUrl,
    telephone: normalizeText(organization.contact_phone) ?? undefined,
    email: normalizeText(organization.contact_email) ?? undefined,
    address: address ?? undefined,
    openingHoursSpecification: openingHours.length ? openingHours : undefined,
  }
}

const canIndexPresentationPage = ({
  organization,
  hasCode,
  page,
}: {
  organization: OrganizationPresentation | null
  hasCode: boolean
  page: OrgPresentationSeoPage
}): boolean =>
  organization?.presentation_indexing_enabled === true &&
  !hasCode &&
  (page === "profile" || page === "business")

export const buildOrgPresentationSeoModel = ({
  organization,
  presentationMode,
  hasCode,
  page,
  presentationBasePath,
  origin,
}: BuildOrgPresentationSeoModelArgs): OrgPresentationSeoModel => {
  const orgLabel = normalizeText(organization?.name) ?? "Betrieb"
  const canonicalUrl = buildCanonicalUrl({ origin, presentationBasePath, page })
  const indexablePage = canIndexPresentationPage({ organization, hasCode, page })
  const title =
    page === "assist"
      ? `KehrAssist bei ${orgLabel} | KehrBlatt`
      : page === "contactMethods"
        ? `Kontaktmittel ${orgLabel} | KehrBlatt`
        : page === "privacy"
          ? `Datenschutz ${orgLabel} | KehrBlatt`
          : page === "business"
            ? `${orgLabel} Betrieb | KehrBlatt`
            : hasCode
              ? `Ihr Termin bei ${orgLabel} | KehrBlatt`
              : `Termin bei ${orgLabel} online abstimmen | KehrBlatt`

  return {
    title,
    description: buildDescription({ organization, presentationMode, hasCode, page }),
    canonicalUrl,
    robots: indexablePage ? "index,follow" : "noindex,follow",
    jsonLd: indexablePage
      ? buildLocalBusinessJsonLd({
          organization,
          canonicalUrl: buildCanonicalUrl({ origin, presentationBasePath, page: "profile" }),
        })
      : null,
  }
}

const appendManagedElement = (element: HTMLElement) => {
  element.setAttribute("data-kehrblatt-presentation-seo", "true")
  document.head.appendChild(element)
}

export const applyOrgPresentationSeo = (args: BuildOrgPresentationSeoModelArgs): (() => void) => {
  if (typeof document === "undefined") return () => undefined

  const previousTitle = document.title
  const model = buildOrgPresentationSeoModel(args)
  document.head
    .querySelectorAll('[data-kehrblatt-presentation-seo="true"]')
    .forEach((element) => element.remove())
  document.title = model.title

  const description = document.createElement("meta")
  description.setAttribute("name", "description")
  description.setAttribute("content", model.description)
  appendManagedElement(description)

  const robots = document.createElement("meta")
  robots.setAttribute("name", "robots")
  robots.setAttribute("content", model.robots)
  appendManagedElement(robots)

  const canonical = document.createElement("link")
  canonical.setAttribute("rel", "canonical")
  canonical.setAttribute("href", model.canonicalUrl)
  appendManagedElement(canonical)

  if (model.jsonLd) {
    const jsonLd = document.createElement("script")
    jsonLd.setAttribute("type", "application/ld+json")
    jsonLd.textContent = JSON.stringify(model.jsonLd)
    appendManagedElement(jsonLd)
  }

  return () => {
    document.head
      .querySelectorAll('[data-kehrblatt-presentation-seo="true"]')
      .forEach((element) => element.remove())
    document.title = previousTitle
  }
}
